import React from "react";
import { FieldBadge } from "../field-badge";
import { Avatar } from "../../../profile_utils";
import { MultiSelectComponent } from "../../../../ui/input_multi_select/controller";

interface Profile {
  id: number;
  name: string;
  logo_url?: string;
  sector?: string;
  sector_id?: number;
  contribution?: number;
  surface_min?: number;
  surface_max?: number;
  surface_display?: string;
  chalandise?: number;
}

interface EnseigneMainProfileProps {
  profile: Profile;
  secteurs?: any[];
}

export function EnseigneMainProfile({
  profile,
  secteurs = [],
}: EnseigneMainProfileProps) {
  const formatCurrency = (value?: number) => {
    if (!value || value === 0) return "non renseigné";
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
    }).format(value);
  };

  const formatNumber = (value?: number) => {
    if (!value || value === 0) return "non renseignée";
    return new Intl.NumberFormat("fr-FR").format(value) + " hab.";
  };

  return (
    <div className="h-full flex flex-col">
      <div className="px-4 py-2 border-b border-slate-400/60 h-[51px] items-center flex">
        <h3 className="font-semibold text-slate-800 text-lg">Votre enseigne</h3>
      </div>

      <div className="p-6 space-y-6">
        {/* Logo and Name */}
        <div className="flex items-center gap-3">
          <Avatar
            entityType="enseigne"
            imageUrl={profile.logo_url}
            name={profile.name}
            size="xl"
          />
          <div className="font-semibold text-slate-900">{profile.name}</div>
        </div>

        {/* Fields */}
        <div className="space-y-3 text-sm">
          {/* Secteur */}
          <div className="flex items-start justify-between gap-4">
            <span className="font-medium text-slate-700 min-w-[100px]">
              Secteur :
            </span>
            <div className="flex-1">
              <FieldBadge
                profileId={profile.id}
                profileType="enseigne"
                fieldName="sector_id"
                label={profile.sector || "non renseigné"}
                value={profile.sector}
              >
                <div className="pt-4">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Secteur d'activité
                  </label>
                  <MultiSelectComponent
                    name="sector_id"
                    selectedIds={profile.sector_id ? [profile.sector_id] : []}
                    options={secteurs}
                    isMulti={false}
                    creatable={false}
                  />
                </div>
              </FieldBadge>
            </div>
          </div>

          {/* Apport */}
          <div className="flex items-start justify-between gap-4">
            <span className="font-medium text-slate-700 min-w-[100px]">
              Apport :
            </span>
            <div className="flex-1">
              <FieldBadge
                profileId={profile.id}
                profileType="enseigne"
                fieldName="contribution"
                label={formatCurrency(profile.contribution)}
                value={profile.contribution}
              >
                <div className="pt-4">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Apport demandé (en €)
                  </label>
                  <input
                    type="number"
                    name="contribution"
                    defaultValue={profile.contribution}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-default-600"
                  />
                </div>
              </FieldBadge>
            </div>
          </div>

          {/* Surface */}
          <div className="flex items-start justify-between gap-4">
            <span className="font-medium text-slate-700 min-w-[100px]">
              Surface :
            </span>
            <div className="flex-1">
              <FieldBadge
                profileId={profile.id}
                profileType="enseigne"
                fieldName="surface_min"
                label={profile.surface_display || "non renseignée"}
                value={profile.surface_min}
              >
                <div className="pt-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Surface minimale (m²)
                    </label>
                    <input
                      type="number"
                      name="surface_min"
                      defaultValue={profile.surface_min}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-default-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Surface maximale (m²)
                    </label>
                    <input
                      type="number"
                      name="surface_max"
                      defaultValue={profile.surface_max}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-default-600"
                    />
                  </div>
                </div>
              </FieldBadge>
            </div>
          </div>

          {/* Chalandise */}
          <div className="flex items-start justify-between gap-4">
            <span className="font-medium text-slate-700 min-w-[100px]">
              Chalandise :
            </span>
            <div className="flex-1">
              <FieldBadge
                profileId={profile.id}
                profileType="enseigne"
                fieldName="chalandise"
                label={formatNumber(profile.chalandise)}
                value={profile.chalandise}
              >
                <div className="pt-4">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Zone de chalandise (habitants)
                  </label>
                  <input
                    type="number"
                    name="chalandise"
                    defaultValue={profile.chalandise}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-default-600"
                  />
                </div>
              </FieldBadge>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
